// This file is deprecated - use useChatAPI.tsx for new API-based communication
// Keeping for backward compatibility with existing LiveKit implementation

import React, { useCallback, useRef, useEffect } from 'react';
import { Room } from 'livekit-client';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useLiveMessageActions } from './useLiveMessages';
import { useGlobalChatState, useCharacterData, useGlobalLiveKitRoomState, useInactivityTimer, useChatStatus } from './useGlobalChatState';
import { getPresignedUrl } from '@/app/lib/actions/s3';
import { ChatStatus } from '../data/globalChatState';
import { parseHints } from '../frontend/shared/textUtil';
import { getCurrentImageModelPrice, getCurrentModelPrice, getCurrentVoicePrice } from '../data/chatSetting/settingOptions';

interface UseDataReceivedParams {
  room: Room | null;
}

interface UseSendMessageParams {
  // No longer need resetInactivityTimer parameter - using centralized timer
}

/**
 * Hook for sending messages with credit checking and deduction
 * Encapsulates all the logic for sending messages (text input and voice transcription)
 */
export function useSendMessage({}: UseSendMessageParams) {
  const messageActions = useLiveMessageActions();
  const { userEmail, userName, isAuthenticated, isLoading: authLoading } = useAuth();
  const { resetInactivityTimer } = useInactivityTimer();
  const { chatStatus } = useChatStatus();
  const [globalChatStateData] = useGlobalChatState();
  
  // User app data hook for gem count (singleton) - only after auth loading completes
  const [userAppDataState, userAppDataActions] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  
  // Get room from global state
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;
  
  const sendMessage = useCallback(async (
    messageContentToSend: string, 
    messageType: 'text' | 'voice' = 'text'
  ) => {
    // Reset inactivity timer when user sends a message
    resetInactivityTimer();

    // Check if user has sufficient credits before sending message
    if (userEmail) {
      try {
        // Use hook data for reactive updates
        const currentModelPrice = getCurrentModelPrice(globalChatStateData.userChatSetting.languageModel);
        let currentVoicePrice = getCurrentVoicePrice(globalChatStateData.userChatSetting.ttsId);
        if (chatStatus !== ChatStatus.LIVE) {
          currentVoicePrice = 0; // Text chat has no voice usage
        }
        const totalPrice = currentModelPrice + currentVoicePrice;
        // Use singleton gem count for faster access
        if (userAppDataState.creditCount + userAppDataState.dailyCreditCount < totalPrice) {
          // Return error object for insufficient credits
          return { success: false, error: 'insufficient_credits' };
        }
      } catch (error) {
        console.error('Error checking credits:', error);
        // If credit check fails, still allow sending message (fail-safe approach)
      }
    }
    
    const messageId = Date.now().toString();
    let messageData;
    
    if (messageContentToSend.trim() === '') {
      // if msg content is empty, request another response from LLM
      messageData = {
        topic: 'user_input',
        type: 'empty_message',
        message_id: messageId
      };
    }
    else{
      // Add user message to text state immediately
      messageActions.addMessage({
        id: messageId,
        content: messageContentToSend,
        role: 'user',
        created_at: new Date().toISOString(),
        sender_id: userEmail ?? 'user email placeholder',
        sender_name: userName ?? 'user name placeholder',
        isStreaming: false,
      });
      messageData = {
        topic: 'user_input',
        type: 'message',
        message: messageContentToSend,
        message_id: messageId
      };
      if (globalChatStateData.debugPlayBack) {
        messageData = {
          topic: 'user_input',
          type: 'play_back',
          message_id: messageId
        };
        console.log("messageData: ", messageData);
      }
    }

    try {
      // Send to LiveKit for model processing using room's publishData method
      if (room && room.state === 'connected') {
        
        await room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(messageData)),
          { reliable: true }
        );
      } else {
        console.error('Room not connected, cannot send message');
        return { success: false, error: 'room_not_connected' };
      }
      
      // Deduct credits after successful send
      if (userEmail) {
        try {
          const currentModelPrice = getCurrentModelPrice(globalChatStateData.userChatSetting.languageModel);
          let currentVoicePrice = getCurrentVoicePrice(globalChatStateData.userChatSetting.ttsId);
          if (chatStatus !== ChatStatus.LIVE) {
            currentVoicePrice = 0; // Text chat has no voice usage
          }
          const totalPrice = currentModelPrice + currentVoicePrice;
          await userAppDataActions.subtractGems(totalPrice);
          console.log(`Deducted ${totalPrice} credits (${currentModelPrice} model + ${currentVoicePrice} voice) for ${messageType} message using model ${globalChatStateData.userChatSetting.languageModel} and voice ${globalChatStateData.userChatSetting.ttsId}`);
        } catch (error) {
          console.error('Error deducting credits:', error);
          // Don't block the user if credit deduction fails
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error(`Error sending ${messageType} message to LiveKit:`, error);
      // If sending fails, we don't deduct credits
      return { success: false, error: 'send_failed' };
    }
  }, [
    room,
    resetInactivityTimer,
    userEmail,
    userName,
    messageActions,
    userAppDataState.creditCount,
  ]);

  return { sendMessage };
}

/**
 * Hook for removing messages
 * Encapsulates the logic for removing messages from local state and notifying LiveKit
 */
export function useRemoveMessageFromChat({}: UseSendMessageParams) {
  const messageActions = useLiveMessageActions();
  const { updateActiveImage } = useUpdateActiveImage();
  const { resetInactivityTimer } = useInactivityTimer();
  
  // Get room from global state
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;
  
  const removeMessageFromChat = useCallback(async (messageId: string) => {
    const lastMessageWithImage = messageActions.getLastMessageWithImage();
    const isDeletingLastMessageWithImage = lastMessageWithImage?.id === messageId;

    messageActions.removeMessage(messageId);
    
    // Reset inactivity timer when user removes a message
    resetInactivityTimer();
    
    // Update the active image after removing message
    const updatedLastMessageWithImage = messageActions.getLastMessageWithImage();
    if (isDeletingLastMessageWithImage && updatedLastMessageWithImage?.imageUrl) {
      await updateActiveImage(updatedLastMessageWithImage?.imageUrl || '');
    }
    
    try {
      // Send to LiveKit for processing using room's publishData method
      if (room && room.state === 'connected') {
        const messageData = {
          topic: 'user_input',
          type: 'remove_message',
          message_id: messageId
        };
        
        await room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(messageData)),
          { reliable: true }
        );
      } else {
        console.error('Room not connected, cannot send remove message');
        return { success: false, error: 'room_not_connected' };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error sending remove message to LiveKit:', error);
      return { success: false, error: 'send_failed' };
    }
  }, [
    room,
    resetInactivityTimer,
    messageActions,
    updateActiveImage,
  ]);

  return { removeMessage: removeMessageFromChat };
}

/**
 * Hook for editing messages
 * Encapsulates the logic for editing messages in local state and notifying LiveKit
 */
export function useEditMessage({}: UseSendMessageParams) {
  const messageActions = useLiveMessageActions();
  const { resetInactivityTimer } = useInactivityTimer();
  
  // Get room from global state
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;
  
  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    // Update local state first
    messageActions.editMessage(messageId, newContent);
    
    // Reset inactivity timer when user edits a message
    resetInactivityTimer();
    
    try {
      // Send to LiveKit for processing using room's publishData method
      if (room && room.state === 'connected') {
        const messageData = {
          topic: 'user_input',
          type: 'edit_message',
          message_id: messageId,
          message: newContent
        };
        
        await room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(messageData)),
          { reliable: true }
        );
      } else {
        console.error('Room not connected, cannot send edit message');
        return { success: false, error: 'room_not_connected' };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error sending edit message to LiveKit:', error);
      return { success: false, error: 'send_failed' };
    }
  }, [messageActions, room, resetInactivityTimer]);

  return { editMessage };
}

/**
 * Hook for updating chat settings
 * Encapsulates the logic for sending chat settings to the LLM backend
 */
export function useUpdateChatSetting() {
  // Get room from global state
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const { resetInactivityTimer } = useInactivityTimer();
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const room = globalLKRoomState.room;
  
  const updateChatSetting = useCallback(async (chatSettings: { llm_model?: string; image_gen?: string; tts_id?: string }) => {
    // Reset inactivity timer for user interaction
    resetInactivityTimer();
    try {
      // Send to LiveKit for processing using room's publishData method
      if (room && room.state === 'connected') {
        // Get current active image URL from global state
        const activeImage = globalChatStateData.activeImage;
        const activeImageUrl = activeImage.imageUri;
        
        const messageData = {
          topic: 'user_setting',
          type: 'chat_setting',
          content: {
            llm_model: chatSettings.llm_model,
            image_gen: chatSettings.image_gen,
            tts_id: chatSettings.tts_id,
            active_image_url: activeImageUrl
          }
        };
        
        await room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(messageData)),
          { reliable: true }
        );
        return { success: true };
      } else {
        return { success: false, error: 'room_not_connected' };
      }
    } catch (error) {
      return { success: false, error: 'send_failed' };
    }
  }, [room, resetInactivityTimer]);

  return { updateChatSetting };
}

// OLD useInactivityTimeout hook removed - now using centralized timer in globalChatState

/**
 * Custom hook that provides the handleDataReceived function for processing LiveKit data
 * Encapsulates all the logic for handling voice transcription, streaming text, and image messages
 * Uses centralized singleton hooks internally to maintain reactivity
 */
export function useDataReceived() {
  // Use singleton hooks internally
  const messageActions = useLiveMessageActions();
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const { character } = useCharacterData();
  const { userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  const [userAppDataState, userAppDataActions] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  
  // Get room from global state
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;
  
  // Use the new send message hook
  const { sendMessage } = useSendMessage({});
  
  // Use hint state hook
  const hintState = useHintState();
  
  // Use the new update active image hook
  const { updateActiveImage } = useUpdateActiveImage();
  
  // Simple string buffer for tokens that arrive before streamingMessageId is set
  const streamingReceiverBuffer = useRef<string>('');
  
  // Derive values from centralized state
  const avatar_name = character?.metadata.avatar_name || 'Unknown Avatar';
  const avatarId = character?.metadata.avatar_id || '';

  const handleDataReceived = useCallback(async (payload: Uint8Array, participant: any) => {
    try {
      const data = JSON.parse(new TextDecoder().decode(payload));
      
      // Handle voice transcription
      if (data.type === 'voice_transcription' && data.resp.index !== undefined) {
        if (data.resp.index <= globalChatStateData.lastProcessedIndexForTranscriptionTracking) {
          return;
        }
        
        globalChatStateActions.setLastProcessedIndexForTranscriptionTracking(data.resp.index);
        console.log('Adding voice transcription to text chat:', data.resp.text);
        
        // Use the centralized send message function
        const result = await sendMessage(data.resp.text, 'voice');
        if (!result.success) {
          console.error('Failed to send voice message:', result.error);
        }
      }
      
      // Handle frontend streaming text (NEW - Direct from LLM for display)
      if (data.topic === 'frontend_stream') {
        if (data.type === 'START') {
          // Start a new streaming message using the message_id from backend
          const messageId = data.message_id || ``;
          globalChatStateActions.setStreamingMessageId(messageId);
          
          // Clear buffer on start
          streamingReceiverBuffer.current = '';
          
          // Add placeholder message
          messageActions.addMessage({
            id: messageId,
            content: '',
            role: 'assistant',
            sender_id: avatarId,
            sender_name: avatar_name,
            created_at: new Date().toISOString(),
            imageUrl: '',
            isStreaming: true,
          });
          
        } else if (data.type === 'DONE' || data.type === 'INTERRUPTED') {
          // Finalize the streaming message
          if (globalChatStateData.streamingMessageId) {
            // Get the complete content from the message in LiveMessageDataHub
            const currentMessage = messageActions.findMessage ? messageActions.findMessage(globalChatStateData.streamingMessageId) : null;
            const completeContent = currentMessage?.content || '';

            // Update streaming status in frontend
            messageActions.updateMessage(globalChatStateData.streamingMessageId, undefined, undefined, false);
            globalChatStateActions.setStreamingMessageId('');

            // Clear buffer on done
            streamingReceiverBuffer.current = '';
          }
        } else if (data.type === 'CONTENT') {
          // Update the streaming message in real-time
          console.log('!! received frontend streaming text: ', data.text, ' streamingMessageId: ', globalChatStateData.streamingMessageId);

          if (globalChatStateData.streamingMessageId) {
            // First append buffer if not empty
            if (streamingReceiverBuffer.current !== '') {
              messageActions.appendMessage(globalChatStateData.streamingMessageId, streamingReceiverBuffer.current);
              streamingReceiverBuffer.current = '';
            }
            // Then append current text
            messageActions.appendMessage(globalChatStateData.streamingMessageId, data.text);
          } else {
            // Buffer the token if streamingMessageId isn't set yet
            streamingReceiverBuffer.current += data.text;
          }
        } else if (data.type === 'IMAGE_URL') {
          // Handle image URL messages from LLM/character
          // the data.imageUrl is the s3_key
          messageActions.addMessage({
            id: data.message_id || '',
            content: data.text || data.caption || '',
            role: 'assistant',
            sender_id: avatarId,
            sender_name: avatar_name,
            created_at: new Date().toISOString(),
            imageUrl: data.imageUrl,
            isStreaming: false,
          });
          
          // Auto-apply new generated image: update and send immediately
          if (data.imageUrl) {
            // await updateActiveImage(data.imageUrl);
            globalChatStateActions.setIsImageGenerating(false);
          }
        } else if (data.type === 'IMAGE_START') {
          // Handle image generation start
          globalChatStateActions.setIsImageGenerating(true);
        } else if (data.type === 'IMAGE_SUCCESS') {
          globalChatStateActions.setIsImageGenerating(false);
          // Deduct credits for image generation
          if (userEmail) {
            try {
              await userAppDataActions.subtractGems(getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel));
              console.log(`Deducted ${getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel)} credits for image generation`);
            } catch (error) {
              console.error('Error deducting credits for image generation:', error);
              // Don't block the user if credit deduction fails
            }
          }
        } else if (data.type === 'IMAGE_CANCEL') {
          // Don't handle different cases yet
          // const cancelContent = data.content || '';
          // console.log('Received IMAGE_CANCEL signal:', cancelContent);
          globalChatStateActions.setIsImageGenerating(false);
        }
      }
      
      // Handle hint response from server
      if (data.topic === 'frontend_hint' && data.type === 'hint') {
        console.log('Received hint response:', data.text);
        hintState.receiveHint(data.text);
      }
      
      // Handle inactivity timeout signal from server
      if (data.topic === 'inactivity_timeout') {
        console.log('[Inactivity] Received server inactivity timeout signal');
        
        // Set inactivity timeout state (components will react to this)
        globalChatStateActions.setInactivityTimeoutState({
          isInactivityTimeoutPopupOpen: true,
          inactivityTimeoutReason: 'server_signal'
        });
      }
    } catch (error) {
      console.error('Error processing received data:', error);
    }
  }, [
    // Only include the external parameters and critical state that affects the callback
    sendMessage,
    // The hooks manage their own internal dependencies automatically
    globalChatStateData.lastProcessedIndexForTranscriptionTracking,
    globalChatStateData.streamingMessageId,
    avatarId,
    avatar_name,
    messageActions,
    globalChatStateActions,
  ]);

  return { handleDataReceived, hintState };
} 

/**
 * Hook for updating both frontend active image and backend image url swap signal
 * Uses useGlobalChatState and useGlobalLiveKitRoomState internally
 */
export function useUpdateActiveImage() {
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  
  const updateActiveImage = useCallback(async (
    imageS3Key: string
  ): Promise<void> => {
    const room = globalLKRoomState.room;
    
    if (!imageS3Key || !room) {
      console.error('updateActiveImage: Missing required parameters');
      return;
    }

    try {
      // Get presigned URL for the image
      const presigned_url = await getPresignedUrl(imageS3Key);
      
      // Send image URL swap signal to backend
      const swapData = {
        type: 'IMAGE_URL_SWAP',
        currentInUse: presigned_url.presignedUrl,
        timestamp: new Date().toISOString()
      };
      
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(swapData)),
        { topic: 'image_url_swap' }
      );
      console.log('!! sent image url swap immediately: ', presigned_url.presignedUrl);
      
      // Update active image in global state (now includes automatic head detection and image swap)
      await globalChatStateActions.setActiveImage(imageS3Key);
      
      // Also send chat setting update with current settings and new active image URL
      const userChatSetting = globalChatStateData.userChatSetting;
      const chatSettingData = {
        topic: 'user_setting',
        type: 'chat_setting',
        content: {
          llm_model: userChatSetting.languageModel,
          image_gen: userChatSetting.imageGenMode === 'auto' ? 'auto' : 'off',
          tts_id: userChatSetting.ttsId,
          active_image_url: imageS3Key
        }
      };
      
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(chatSettingData)),
        { reliable: true }
      );
      console.log('!! sent chat setting update with new active image URL: ', imageS3Key);
      
    } catch (error) {
      console.error('Error updating active image:', error);
    }
  }, [globalChatStateActions, globalLKRoomState.room]);

  return { updateActiveImage };
}

/**
 * Hook for managing hint state and functionality using global state
 */
export function useHintState() {
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const { resetInactivityTimer } = useInactivityTimer();
  
  const requestHint = useCallback(async (room: Room | null) => {
    // Reset inactivity timer for user interaction
    resetInactivityTimer();
    
    if (!room || room.state !== 'connected') {
      console.error('Room not connected, cannot request hint');
      return;
    }
    
    // If we can't generate hint and panel is not shown, just toggle panel
    if (!globalChatStateData.canGenerateHint && !globalChatStateData.showHintPanel) {
      globalChatStateActions.setShowHintPanel(true);
      return;
    }

    // If we can't generate hint and panel is shown, just hide panel
    if (!globalChatStateData.canGenerateHint && globalChatStateData.showHintPanel) {
      globalChatStateActions.setShowHintPanel(false);
      return;
    }
    
    // If we can generate hint, request new hints
    try {
      globalChatStateActions.setHintLoading(true);
      globalChatStateActions.setShowHintPanel(true);
      globalChatStateActions.setHints([]);
      
      // Send hint request to LiveKit
      const hintRequestData = {
        topic: 'user_input',
        type: 'require_hint'
      };
      
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(hintRequestData)),
        { reliable: true }
      );
      
      console.log('Hint request sent to LiveKit');
    } catch (error) {
      console.error('Error requesting hint:', error);
      globalChatStateActions.setHintLoading(false);
      globalChatStateActions.setShowHintPanel(false);
    }
  }, [globalChatStateData.canGenerateHint, globalChatStateData.showHintPanel, globalChatStateActions, resetInactivityTimer]);
  
  const receiveHint = useCallback((hintContent: string) => {
    const parsedHints = parseHints(hintContent);
    globalChatStateActions.setHints(parsedHints);
    globalChatStateActions.setHintLoading(false);
    globalChatStateActions.setCanGenerateHint(false); // Flip to false after receiving hints
    console.log('Received hints:', parsedHints);
  }, [globalChatStateActions]);
  
  const closeHintPanel = useCallback(() => {
    globalChatStateActions.setShowHintPanel(false);
  }, [globalChatStateActions]);
  
  const selectHint = useCallback((hintText: string, onSelect: (text: string) => void) => {
    onSelect(hintText);
    // Don't close panel, allow multiple selections
    // Panel stays open so user can select different hints
  }, []);
  
  const resetHintState = useCallback(() => {
    globalChatStateActions.resetHintState();
  }, [globalChatStateActions]);
  
  return {
    isHintLoading: globalChatStateData.isHintLoading,
    hints: globalChatStateData.hints,
    showHintPanel: globalChatStateData.showHintPanel,
    canGenerateHint: globalChatStateData.canGenerateHint,
    requestHint,
    receiveHint,
    closeHintPanel,
    selectHint,
    resetHintState
  };
} 

// implement manual image generation
export function useManualImageGeneration() {
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const { resetInactivityTimer } = useInactivityTimer();
  const [globalChatStateData] = useGlobalChatState();
  const room = globalLKRoomState.room;

  const sendManualImageGen = useCallback(async (imageEditPrompt: string) => {
    // Reset inactivity timer for user interaction
    resetInactivityTimer();
    if (!room || room.state !== 'connected') {
      console.error('Room not connected, cannot send manual image generation request');
      return { success: false, error: 'Room not connected' };
    }

    try {
      // Get current image model from global state
      const currentImageModel = globalChatStateData.userChatSetting.imageModel;
      
      // Send manual image generation request to LiveKit
      const manualImageGenData = {
        topic: 'user_input',
        type: 'manual_image_gen',
        image_edit_prompt: imageEditPrompt,
        image_gen_model: currentImageModel
      };
      
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(manualImageGenData)),
        { reliable: true }
      );
      
      console.log('Manual image generation request sent to LiveKit:', imageEditPrompt);
      return { success: true };
    } catch (error) {
      console.error('Error sending manual image generation request:', error);
      return { success: false, error: 'Failed to send request' };
    }
  }, [room, resetInactivityTimer]);

  return { sendManualImageGen };
}